<template>
  <ViewContainer :title="$t('teams.teams')">
    <template v-slot:header-right>
      <CreateButton
        data-testid="teams-button-create"
        v-if="permissions.create"
        small
        @click="isTeamDialogVisible = true"
      />
    </template>

    <v-card class="py-4" style="min-height: 600px">
      <v-row class="pb-4 px-4 ma-0 d-flex align-center">
        <v-col
          cols="12"
          lg="8"
          md="8"
          :class="{ 'mb-3': $vuetify.breakpoint.smAndDown }"
        >
          <v-chip
            data-testid="teams-chip-myTeams"
            class="mr-4"
            :color="selectedFilter === FILTERS.MY_TEAMS ? 'primary' : ''"
            :outlined="selectedFilter !== FILTERS.MY_TEAMS"
            @click="selectFilter(FILTERS.MY_TEAMS)"
          >
            {{ $t('teams.filter_my_teams') }}
          </v-chip>

          <v-chip
            data-testid="teams-chip-leadByMe"
            class="mr-4"
            :color="selectedFilter === FILTERS.LEAD_BY_ME ? 'primary' : ''"
            :outlined="selectedFilter !== FILTERS.LEAD_BY_ME"
            @click="selectFilter(FILTERS.LEAD_BY_ME)"
          >
            {{ $t('teams.filter_teams_i_lead') }}
          </v-chip>

          <v-chip
            data-testid="teams-chip-allTeams"
            class="mr-4"
            :color="selectedFilter === FILTERS.ALL_TEAMS ? 'primary' : ''"
            :outlined="selectedFilter !== FILTERS.ALL_TEAMS"
            @click="selectFilter(FILTERS.ALL_TEAMS)"
          >
            {{ $t('teams.filter_all_teams') }}
          </v-chip>
        </v-col>

        <v-col cols="12" lg="4" md="4" sm="12" class="d-flex justify-end">
          <SearchInput :value="searchTerms" @change="handleSearchTermsChange" />
        </v-col>
      </v-row>
      <v-divider class="ma-0 mb-4" />
      <div v-if="isLoading">
        <v-row class="px-6 pt-6 pb-2">
          <v-col
            v-for="(team, idx) in 8"
            :key="idx"
            class="mb-3"
            cols="12"
            lg="3"
            md="4"
            sm="6"
            xs="12"
          >
            <TeamCardSkeleton />
          </v-col>
        </v-row>
      </div>

      <div v-if="!isLoading && teams.length > 0">
        <v-row class="px-6 pt-6 pb-2">
          <v-col
            v-for="item in teams"
            :key="item.team._id"
            class="mb-3"
            cols="12"
            lg="3"
            md="4"
            sm="6"
            xs="12"
          >
            <TeamCard
              :team="item.team"
              :highlightTerm="searchTerms"
              :matchingFields="item.matchingFields"
              @deleted="fetchTeams"
              @updated="fetchTeams"
            />
          </v-col>
        </v-row>

        <v-row v-if="totalPages > 1" class="d-flex justify-center pb-4">
          <v-pagination
            circle
            total-visible="10"
            :length="totalPages"
            :value="currentPage"
            @input="handleChangePage"
          />
        </v-row>
      </div>

      <EmptyState
        v-if="teams.length === 0 && !isLoading"
        class="h-100"
        style="min-height: 480px"
        :icon="emptyStateIcon"
        :title="emptyStateTitle"
        :message="emptyStateMessage"
        :buttonText="permissions.create ? $t('common.button.create') : null"
        @click="isTeamDialogVisible = true"
      />
    </v-card>

    <TeamFormDialog
      v-if="isTeamDialogVisible"
      :show="isTeamDialogVisible"
      :organizationId="organizationId"
      :siteId="siteId"
      @close="isTeamDialogVisible = false"
      @created="handleTeamCreated"
    />
  </ViewContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import teamPermissions from '@/mixins/teamPermissions.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import SearchInput from '@/components/Core/SearchInput.vue'
import CreateButton from '@/components/UI/CreateButton.vue'
import TeamFormDialog from '@/components/TeamFormDialog.vue'
import EmptyState from '@/components/EmptyState.vue'
import TeamCard from './TeamCard.vue'
import TeamCardSkeleton from './TeamCardSkeleton.vue'

const FILTERS = {
  MY_TEAMS: 'myTeams',
  LEAD_BY_ME: 'leadByMe',
  ALL_TEAMS: 'allTeams',
}

const ITEMS_PER_PAGE = 12

export default {
  name: 'TeamsView',

  mixins: [mixin, teamPermissions, trackerMixin],

  components: {
    CreateButton,
    EmptyState,
    SearchInput,
    TeamCard,
    TeamCardSkeleton,
    TeamFormDialog,
    ViewContainer,
  },

  data() {
    return {
      currentPage: this.$route.query.currentPage
        ? Number(this.$route.query.currentPage)
        : 1,
      FILTERS,
      isLoading: true,
      isTeamDialogVisible: false,
      searchTerms: this.$route.query.searchTerms ?? '',
      selectedFilter: this.$route.query.selectedFilter ?? FILTERS.MY_TEAMS,
      teams: [],
      totalPages: 0,
    }
  },

  computed: {
    ...mapGetters(['organizationId', 'accountId', 'siteId']),

    permissions() {
      return this.getTeamPermissions({
        team: {
          site: this.siteId,
          leaders: [],
        },
      })
    },

    emptyStateTitle() {
      if (this.searchTerms) return this.$t('teams.search_no_results_title')

      switch (this.selectedFilter) {
        case FILTERS.MY_TEAMS:
          return this.$t('teams.empty_state_title_my_teams')
        case FILTERS.LEAD_BY_ME:
          return this.$t('teams.empty_state_title_lead_by_me')
        case FILTERS.ALL_TEAMS:
          return this.$t('teams.empty_state_title_all_teams')
        default:
          return ''
      }
    },

    emptyStateMessage() {
      if (this.searchTerms) return this.$t('teams.search_no_results_message')

      switch (this.selectedFilter) {
        case FILTERS.MY_TEAMS:
          return this.permissions.create
            ? this.$t('teams.empty_state_message_my_teams_permissions')
            : this.$t('teams.empty_state_message_my_teams')
        case FILTERS.LEAD_BY_ME:
          return this.permissions.create
            ? this.$t('teams.empty_state_message_lead_by_me_permissions')
            : this.$t('teams.empty_state_message_lead_by_me')
        case FILTERS.ALL_TEAMS:
          return this.permissions.create
            ? this.$t('teams.empty_state_message_all_teams_permissions')
            : this.$t('teams.empty_state_message_all_teams')
        default:
          return ''
      }
    },

    emptyStateIcon() {
      return this.searchTerms ? 'mdi-ghost-outline' : 'mdi-beehive-outline'
    },
  },

  mounted() {
    this.fetchTeams()
  },

  methods: {
    handleSearchTermsChange(terms) {
      this.searchTerms = terms ?? ''
      this.currentPage = 1
      this.trackTeamsSearch({ searchTerms: this.searchTerms })
      this.fetchTeams()
    },

    handleChangePage(page) {
      if (page === this.currentPage) return

      this.currentPage = page
      this.fetchTeams()
    },

    selectFilter(filter) {
      this.selectedFilter = filter
      this.currentPage = 1
      this.trackTeamNavigate({ section: this.selectedFilter })
      this.fetchTeams()
    },

    getRequestFilters() {
      switch (this.selectedFilter) {
        case FILTERS.MY_TEAMS:
          return {
            members: this.accountId,
          }
        case FILTERS.LEAD_BY_ME:
          return {
            leaders: [this.accountId],
          }
        case FILTERS.ALL_TEAMS:
        default:
          return {}
      }
    },

    async fetchTeams() {
      try {
        this.isLoading = true
        this.updateQueryParams()

        const start = (this.currentPage - 1) * ITEMS_PER_PAGE
        const queryParams = {
          filters: this.getRequestFilters(),
          limit: ITEMS_PER_PAGE,
          from: start,
          sort: { by: '_score', order: 'desc' },
        }

        if (this.searchTerms) queryParams.terms = this.searchTerms
        const response = await api.teams.getTeamsStream(
          this.organizationId,
          { ...queryParams, includeMatchingFields: true }
        )
        this.teams = response.data?.results ?? []
        this.totalPages =
          Math.ceil(response?.data?.totalRecordsInData / ITEMS_PER_PAGE) || 0
      } catch (err) {
        this.teams = []
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    updateQueryParams() {
      this.$router
        .replace({
          query: {
            currentPage: this.currentPage,
            searchTerms: this.searchTerms,
            selectedFilter: this.selectedFilter,
          },
        })
        .catch(() => {
          // This is intentional to avoid navigation duplicated error
        })
    },

    handleTeamCreated(team) {
      this.navigateTo({
        name: 'teamDetails',
        params: { teamId: team.id },
      })
    },
  },
}
</script>
